<script lang="ts">
  import { onDestroy, onMount, tick } from 'svelte'
  import filters, { isChanged, isLoading, submitBtnText } from './lib/stores/filters'
  import FilterDescription from './components/FilterDescription.svelte'
  import LocationCategory from './components/LocationCategory.svelte'
  import RegionCategory from './components/RegionCategory.svelte'
  import FilterSelected from './components/FilterSelected.svelte'
  import PriceCategory from './components/PriceCategory.svelte'
  import FilterFooter from './components/FilterFooter.svelte'
  import TypeCategory from './components/TypeCategory.svelte'
  import TermCategory from './components/TermCategory.svelte'
  import useActiveCategory from './hooks/use-active-category'
  import FilterHeader from './components/FilterHeader.svelte'
  import FilterBody from './components/FilterBody.svelte'
  import FilterNav from './components/FilterNav.svelte'
  import Loader from './components/Loader.svelte'
  import Button, { COLOR } from './components/Button.svelte'
  import Image from './components/Image.svelte'
  import useFilter from './hooks/use-filter'
  import { fade } from 'svelte/transition'
  import type { Filter } from './global'
  import { parse } from 'parse-form'
  import Client from './lib/client'

  let form: HTMLFormElement
  let isMounted = false
  let isAutoSubmit = false
  let showLoader = false
  let requestsSent = 0
  let {
    selectedFilters,
    hiddenInputs,
    categories,
    isSimpleView,
    action,
    enabledPrefetch,
    urlPrefetch,
    buttons,
  } = useFilter()

  const initialFilters = JSON.stringify($filters)
  const categoriesBody = {
    region: RegionCategory,
    location: LocationCategory,
    price: PriceCategory,
    type: TypeCategory,
    term: TermCategory,
  }

  const { removeAll, removeFilter } = filters
  const { closeActive } = useActiveCategory()
  const client = new Client()

  const prefetch = async () => {
    // the form can be updated via "filters" store, so we
    // need to wait for the DOM to be in sync with it
    await tick()

    if (enabledPrefetch) {
      const { body } = parse(form)
      const url = `${location.origin}${urlPrefetch}`
      return client.get(url, body)
    }
  }

  const onRemoveAll = () => {
    removeAll(selectedFilters.items)
    selectedFilters.items = []
    isAutoSubmit = true
  }

  const onRemoveFilter = (event: CustomEvent<Filter.SelectedFilter>) => {
    const { detail } = event
    removeFilter(detail)
    selectedFilters.items = selectedFilters.items.filter(
      ({ key, value }) => `${key}${value}` !== `${detail.key}${detail.value}`
    )
    isAutoSubmit = true
  }

  const unsubscribe = filters.subscribe(() => {
    if (isMounted) {
      requestsSent += 1
      // finally() is a valid function call
      // @ts-ignore
      prefetch()
        .then((data: { action: string; submitBtnText: string }) => {
          if (data) {
            form.action = data.action
            $submitBtnText = data.submitBtnText
          }
        })
        .catch(console.error)
        .finally(() => (requestsSent -= 1))
    }
  })

  const onSubmit = (event: SubmitEvent) => {
    if ($isChanged) {
      showLoader = true
    } else {
      event.preventDefault()
      closeActive()
    }
  }

  const onOrderChange = () => {
    form.submit()
    showLoader = true
  }

  const onClick = () => {
    // allow to send the form even when no changes occurred
    form.submit()
    showLoader = true
  }

  const onPageShow = (event: PageTransitionEvent) => {
    // most browsers cache pages so when you navigate back,
    // they remember the last state
    if (event.persisted) {
      showLoader = false
    }
  }

  onMount(() => {
    isMounted = true
    form.action = action
  })
  onDestroy(unsubscribe)

  $: $isChanged = initialFilters !== JSON.stringify($filters)
  $: showLoader = isAutoSubmit
  $: $isLoading = requestsSent > 0
  $: isAutoSubmit && requestsSent === 0 && form.submit()
</script>

<svelte:window on:pageshow={onPageShow} />

<div class="w-full" transition:fade>
  <form method="get" autocomplete="off" bind:this={form} on:submit={onSubmit}>
    <!--"filter" is used in robots.txt for SEO purposes-->
    <input type="hidden" name="filter" />
    {#each hiddenInputs as { name, value }}
      <input type="hidden" {name} {value} />
    {/each}
    <div class="flex w-full gap-x-1">
      <FilterHeader>
        {#each Object.values(categories) as category}
          <FilterNav {category} />
        {/each}
      </FilterHeader>
      {#if isSimpleView}
        <Button
          class="hidden sm:!flex"
          color={COLOR.WARNING}
          on:click={onClick}
          title={buttons.searchAccommodation}
        >
          <Image src="/share/img/ico/search.svg" width="16" height="16" />
        </Button>
      {/if}
    </div>
    <FilterBody>
      {#each Object.values(categories) as { id }}
        <svelte:component this={categoriesBody[id]} />
      {/each}
    </FilterBody>
    {#if !isSimpleView}
      {#if selectedFilters.items.length > 0}
        <FilterSelected
          {...selectedFilters}
          on:removeall={onRemoveAll}
          on:removefilter={onRemoveFilter}
        />
      {:else}
        <FilterDescription />
      {/if}
      <FilterFooter on:orderchange={onOrderChange} />
    {:else}
      <Button class="mx-auto mt-2 px-10 sm:hidden" color={COLOR.WARNING} on:click={onClick}>
        {buttons.searchAccommodation}
      </Button>
    {/if}
  </form>
</div>
{#if showLoader}
  <Loader />
{/if}
